import { useState, useEffect, useMemo } from "react";
import { useOracle } from "@/contexts/OracleContext";
import { OracleToggle } from "@/components/shared/OracleToggle"; // Importe o toggle
import { TroquinhoCalendar } from "@/components/shared/TroquinhoCalendar";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Sparkles, ChevronLeft, ChevronRight, Calendar,
  User, Users, TrendingUp, TrendingDown,
  ClipboardCheck, Plus, Minus, BarChart3,
  UserPlus, Bell, Settings, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { useAuth } from "@/contexts/AuthContext";
import { subscribeToTransactions } from "@/services/transactionsService";
import { parseISO } from "date-fns";

// Inspirational and fun quotes
const dailyQuotes = [
  "Dinheiro bem organizado é liberdade conquistada.",
  "Pequenas economias de hoje, grandes conquistas amanhã.",
  "Controle suas finanças antes que elas controlem você.",
  "Cada centavo conta na jornada para seus sonhos.",
  "Organização financeira é o primeiro passo para a tranquilidade.",
  "Organizar o dinheiro: menos emoção, mais decisão.",
  "Você até pode ignorar suas finanças… elas não vão te ignorar.",
  "Prometo ser gentil… mas seu extrato talvez não seja.",
  "Liberdade financeira começa quando você para de fingir que não viu a fatura.",
  "Pequenos gastos: inocentes sozinhos, perigosos em grupo.",
  "Seu dinheiro não some. Ele só vai pra lugares que você não lembra.",
  "Planejar não é chato. Chato é ficar sem dinheiro.",
  "Se você não controla, o saldo controla você.",
  "Guardar dinheiro: o ato revolucionário de não torrar tudo.",
  "A paz financeira começa quando você encara os números sem drama.",
  "Dinheiro organizado = menos susto, mais escolha.",
  "Não é sobre ganhar mais. É sobre parar de perder sem perceber.",
  "Seu futuro agradece cada decisão menos impulsiva de hoje.",
  "Parcelado parece leve… até virar uma coleção.",
  "Controle financeiro: porque ‘depois eu vejo’ nunca resolve.",
  "Gastar sem pensar é fácil. Difícil é entender depois.",
  "Você trabalha pelo dinheiro ou ele trabalha contra você?",
  "Organizar hoje pra não sofrer no dia 5.",
  "Ignorar o saldo não faz ele melhorar, só piora a surpresa.",
  "Cada real tem um destino. Ou você escolhe, ou ele escolhe por você.",
  "Ser adulto é basicamente gerenciar boletos com dignidade.",
  "Spoiler: ignorar o saldo não melhora o saldo.",
  "Modo adulto ativado: olhar a fatura sem fechar o app.",
  "Parcelar é fácil. Lembrar depois… nem tanto.",
  "Se organizar direitinho, dá até pra gastar sem culpa.",
  "Sem caos, sem surto, só um controle básico mesmo.",
  "A conta chega. A gente te ajuda a não fingir que não viu.",
  "Não é sobre cortar tudo… é sobre parar de se sabotar.",
  "Seu extrato contando histórias que você não lembra de ter vivido.",
  "Gastar você já sabe. Bora aprender a acompanhar também.",
  "Controle financeiro, mas sem vibe planilha de 2005.",
  "Seu futuro financeiro agradece esse mínimo esforço aqui.",
  "Não é dieta financeira. É só parar de exagerar sem perceber.",
  "Organizar o dinheiro: menos ‘eita’, mais ‘tá tranquilo’."
];

// Format currency helper
const formatCurrency = (value) => {
  return value.toLocaleString('pt-BR', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  });
};

// Months in Portuguese
const months = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

export default function Home() {
  const { user, userProfile } = useAuth();
  const [viewMode, setViewMode] = useState("individual");
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const monthKey = `${currentYear}-${currentMonth}`;
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { isOracleActive } = useOracle();

  // Sincroniza o calendário com o Modo Oráculo
  useEffect(() => {
    const hoje = new Date();
    if (isOracleActive) {
      // Força para o mês seguinte (Visão Oráculo)
      const dataFutura = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 1);
      setCurrentMonth(dataFutura.getMonth());
      setCurrentYear(dataFutura.getFullYear());
    } else {
      // Retorna para o mês real (Visão Normal)
      setCurrentMonth(hoje.getMonth());
      setCurrentYear(hoje.getFullYear());
    }
  }, [isOracleActive]);
  
  // Get random daily quote (memoized to avoid re-render changes)
  const dailyQuote = useMemo(() => {
  // Usa o dia do ano como semente para a frase mudar apenas 1x por dia
  const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
  return dailyQuotes[dayOfYear % dailyQuotes.length];
}, []);

  // Get user's first name
  const userName = userProfile?.name?.split(' ')[0] || user?.displayName?.split(' ')[0] || "Usuário";

// Subscribe to transactions
  useEffect(() => {
    if (!user?.uid) return;
    
    if (transactions.length === 0) setIsLoading(true);
    
    const unsubscribe = subscribeToTransactions(
      user.uid, 
      currentMonth, 
      currentYear, 
      (data) => {
        setTransactions(data);
        setIsLoading(false);
      },
      () => setIsLoading(false)
    );

    return () => unsubscribe();
  }, [user?.uid, currentMonth, currentYear]);

  // Calculate financial summary for selected month
  const summary = useMemo(() => {
    const monthTransactions = transactions.filter(t => {
      const tDate = parseISO(t.dueDate || t.date);
      return tDate.getMonth() === currentMonth && tDate.getFullYear() === currentYear;
    });

    const income = monthTransactions
      .filter(t => t.type === "income")
      .reduce((sum, t) => sum + (viewMode === "individual" ? (t.myShare ?? 0) : t.amount), 0);
    
    const expenses = monthTransactions
      .filter(t => t.type === "expense")
      .reduce((sum, t) => {
        const value = viewMode === "individual" ? (t.myShare ?? 0) : (t.amount ?? 0);
        return sum + (Number(value) || 0);
      }, 0);

    return {
      balance: income - expenses,
      income,
      expenses
    };
  }, [transactions, currentMonth, currentYear, viewMode]);

  // Pending settlements count
  const pendingSettlements = useMemo(() => {
    const savedChecked = JSON.parse(localStorage.getItem("troquinho_checklist_checked") || "{}");
    const expenses = transactions.filter(t => t.type === "expense");
    const pendingGroups = new Set();

    expenses.forEach(t => {
      let itemId = "";
      if (t.paymentMethod === "boleto") itemId = `boleto-${t.id}`;
      else if (["pix", "debit", "debito"].includes(t.paymentMethod)) itemId = `bank-${t.bankId || 'unknown'}`;
      else if (["credit", "credito"].includes(t.paymentMethod)) itemId = `card-${t.cardId || 'unknown'}`;
      else if (["cash", "dinheiro"].includes(t.paymentMethod)) itemId = `cash-group`;
      else itemId = `boleto-${t.id}`;

      if (!savedChecked[`${monthKey}-${itemId}`]) {
        pendingGroups.add(itemId);
      }
    });

    return pendingGroups.size;
  }, [transactions, currentMonth, currentYear, monthKey]);


  return (
    <div className="min-h-screen bg-slate-50 pb-32">
      <div className="max-w-lg mx-auto px-5 pt-6">
        
        {/* Header Expressivo */}
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-6"
        >
          <div>
            <p className="text-indigo-500 font-medium text-sm">Olá,</p>
            <h1 className="text-xl font-black tracking-tight text-slate-900">
              {userName}!
            </h1>
          </div>
          <div className="flex gap-2">
            <Link to="/notifications">
  <Button 
    variant="ghost" 
    size="icon" 
    className="rounded-full bg-white shadow-sm hover:bg-indigo-50 transition-colors"
    data-testid="notifications-btn"
  >
    {/* Trocamos o sino pelo Sparkles com a cor Indigo para remeter ao Oráculo */}
    <Sparkles className="w-5 h-5 text-indigo-500" />
  </Button>
</Link>
            <Link to="/profile">
              <Button 
                variant="ghost" 
                size="icon" 
                className="rounded-full bg-white shadow-sm"
                data-testid="settings-btn"
              >
                <Settings className="w-5 h-5 text-slate-600" />
              </Button>
            </Link>
          </div>
        </motion.header>

        {/* Daily Quote Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-[24px] p-4 mb-5 flex items-center gap-4"
          data-testid="daily-quote-card"
        >
          <div className="w-10 h-10 rounded-2xl bg-violet-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-500/30">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <p className="text-sm font-medium text-slate-700 leading-snug">
            "{dailyQuote}"
          </p>
        </motion.div>

        {/* Barra de Controles - Ajustada */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-col gap-4 mb-6"
        >
          <div className="flex items-center justify-between w-full gap-2">
            
            {/* Calendário à esquerda */}
            <div className="flex-shrink-0">
              <TroquinhoCalendar 
                currentMonth={currentMonth} 
                currentYear={currentYear} 
                onPeriodChange={(month, year) => {
                  setCurrentMonth(month);
                  setCurrentYear(year);
                }} 
              />
            </div>
            
{/* Toggle à direita - Somente Ícones */}
            <div className="flex items-center bg-white rounded-full p-1 shadow-sm border border-slate-100 flex-shrink-0 h-12">
              <button
                onClick={() => setViewMode("individual")}
                className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                  viewMode === "individual"
                    ? "bg-indigo-500 text-white shadow-lg shadow-indigo-200"
                    : "text-slate-400 hover:bg-slate-50"
                }`}
                title="Visão Individual"
              >
                <User className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode("grupo")}
                className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                  viewMode === "grupo"
                    ? "bg-indigo-500 text-white shadow-lg shadow-indigo-200"
                    : "text-slate-400 hover:bg-slate-50"
                }`}
                title="Visão Grupo"
              >
                <Users className="w-5 h-5" />
              </button>
            </div>

          </div>
        </motion.div>

        {/* Card de Saldo - O Destaque */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.15 }}
          className={`relative overflow-hidden rounded-[32px] p-5 mb-5 shadow-lg transition-all duration-700 ${
  isOracleActive 
    ? "bg-gradient-to-br from-purple-600 to-indigo-700 shadow-purple-500/40" 
    : summary.balance >= 0 
      ? "bg-gradient-to-br from-emerald-400 to-teal-500 shadow-emerald-500/20" 
      : "bg-gradient-to-br from-rose-400 to-rose-500 shadow-rose-500/20"
}`}
          data-testid="balance-card"
        >
          {/* Decorative Blobs */}
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
          <div className="absolute -bottom-16 -left-16 w-32 h-32 bg-violet-400/20 rounded-full blur-2xl" />
          <div className="absolute top-1/2 right-10 w-24 h-24 bg-indigo-300/10 rounded-full blur-xl" />
          
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <span translate="no" className="text-white/80 text-sm font-medium">
                Saldo {viewMode === "grupo" ? "do Grupo" : "Atual"}
              </span>
              <div className="px-2 py-1 bg-white/20 rounded-full text-xs text-white font-medium">
                {months[currentMonth]}
              </div>
            </div>
            {isLoading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-6 h-6 text-white animate-spin" />
                <span className="text-white/60">Carregando...</span>
              </div>
            ) : (
              <>
                <p className="text-[40px] font-black tracking-tight text-white leading-none mb-1">
                  R$ {formatCurrency(summary.balance)}
                </p>
                <p className="text-white/60 text-sm">
                  Atualizado agora
                </p>
              </>
            )}
          </div>
        </motion.div>

        {/* Grid de Receitas/Despesas */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 gap-3 mb-5"
        >
          {/* Receitas */}
          <div className="bg-white rounded-[24px] p-4 shadow-sm border border-slate-100" data-testid="income-card">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-1">Receitas</p>
            <p className="text-xl font-black tracking-tight text-emerald-600">
              R$ {formatCurrency(summary.income)}
            </p>
          </div>

          {/* Despesas */}
          <div className="bg-white rounded-[24px] p-4 shadow-sm border border-slate-100" data-testid="expenses-card">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-rose-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-1">Despesas</p>
            <p className="text-xl font-black tracking-tight text-rose-600">
              R$ {formatCurrency(summary.expenses)}
            </p>
          </div>
        </motion.div>

        {/* Card Contas & Consequências - Full Width */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <Link to="/payment-checklist">
            <div 
              className="relative overflow-hidden bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-[24px] p-4 mb-5 shadow-lg shadow-indigo-500/20"
              data-testid="settlement-card"
            >
              {/* Decorative Elements */}
              <div className="absolute -right-8 -bottom-8 w-32 h-32 bg-white/10 rounded-full blur-xl" />
              
              <div className="relative z-10 flex items-center gap-4">
                <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                  <ClipboardCheck className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-bold text-lg">Contas & Consequências</h3>
                  <p className="text-white/70 text-sm">
                    {pendingSettlements === 0 
                      ? "Nenhuma pendência 🎉" 
                      : `${pendingSettlements} pendência${pendingSettlements > 1 ? 's' : ''} para resolver`}
                  </p>
                </div>
                <ChevronRight className="w-6 h-6 text-white/60" />
              </div>
            </div>
          </Link>
        </motion.div>

        {/* Atalhos Bento Evoluídos */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-col gap-3"
        >
          {/* Linha 1: Entrada e Saída */}
          <div className="grid grid-cols-2 gap-3">
            <Link to="/transactions" state={{ openModal: 'income' }}>
              <div className="bg-emerald-50 rounded-[24px] p-4 shadow-sm border border-emerald-100 hover:shadow-md transition-shadow h-full">
                <div className="w-10 h-10 rounded-2xl bg-emerald-500 flex items-center justify-center mb-3 shadow-lg shadow-emerald-500/30">
                  <Plus className="w-6 h-6 text-white" />
                </div>
                <p translate="no" className="font-bold text-slate-800">Entrada</p>
                <p className="text-xs text-slate-500">Registrar receita</p>
              </div>
            </Link>

            <Link to="/transactions" state={{ openModal: 'expense' }}>
              <div className="bg-rose-50 rounded-[24px] p-4 shadow-sm border border-rose-100 hover:shadow-md transition-shadow h-full">
                <div className="w-10 h-10 rounded-2xl bg-rose-500 flex items-center justify-center mb-3 shadow-lg shadow-rose-500/30">
                  <Minus className="w-6 h-6 text-white" />
                </div>
                <p translate="no" className="font-bold text-slate-800">Saída</p>
                <p className="text-xs text-slate-500">Registrar despesa</p>
              </div>
            </Link>
          </div>

          {/* Linha 3: Conexões e Relatórios */}
          <div className="grid grid-cols-2 gap-3">
            <Link to="/connections">
              <div className="bg-sky-50 rounded-[24px] p-4 shadow-sm border border-sky-100 hover:shadow-md transition-shadow h-full">
                <div className="w-10 h-10 rounded-2xl bg-sky-500 flex items-center justify-center mb-3 shadow-lg shadow-sky-500/30">
                  <UserPlus className="w-6 h-6 text-white" />
                </div>
                <p translate="no" className="font-bold text-slate-800">Conexões</p>
                <p className="text-xs text-slate-500">Pessoas e cartões</p>
              </div>
            </Link>

            <Link to="/reports">
              <div className="bg-amber-50 rounded-[24px] p-4 shadow-sm border border-amber-100 hover:shadow-md transition-shadow h-full">
                <div className="w-10 h-10 rounded-2xl bg-amber-500 flex items-center justify-center mb-3 shadow-lg shadow-amber-500/30">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <p translate="no" className="font-bold text-slate-800">Relatórios</p>
                <p className="text-xs text-slate-500">Análise mensal</p>
              </div>
            </Link>
          </div>
        </motion.div>
        {/* Botão Flutuante do Oráculo */}
        <OracleToggle />

      </div>
    </div>
  );
}
